import React, { useEffect, useMemo, useState, useRef } from 'react';
import { ebosApi, brandsApi, downloadAuthed } from '../api';
import PageHeader from '../components/PageHeader';
import BulkAssignBrandModal from '../components/BulkAssignBrandModal';
import ImportProgressModal from '../components/ImportProgressModal';
import { Download, Upload, Plus, Phone, Mail, Loader2, X, Pencil, Trash2, Power, Search, ArrowUpDown, Tag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DEFAULT_DRAFT = { code: '', name: '', zone: 'North', state: '', city: '', contact: '', email: '', gst: '', brand: '' };
const SORT_OPTIONS = [
  { key: 'name-asc', label: 'Name · A → Z' },
  { key: 'name-desc', label: 'Name · Z → A' },
  { key: 'code-asc', label: 'Branch code · A → Z' },
  { key: 'code-desc', label: 'Branch code · Z → A' },
];

export default function EBOMaster() {
  const { user, theme } = useAuth();
  const [ebos, setEbos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [msg, setMsg] = useState('');
  const fileRef = useRef();
  const [importState, setImportState] = useState({ open: false, phase: 'uploading', progress: 0, fileName: '', result: null, errorMsg: '' });

  // filters + sort
  const [q, setQ] = useState('');
  const [zone, setZone] = useState('all');
  const [state, setState] = useState('all');
  const [city, setCity] = useState('all');
  const [sort, setSort] = useState('name-asc');

  const load = () => { setLoading(true); ebosApi.list().then(setEbos).finally(() => setLoading(false)); };
  useEffect(load, []);

  // ---------- selection + bulk actions ----------
  const [selected, setSelected] = useState(() => new Set());
  const toggleOne = (code) => setSelected(prev => {
    const n = new Set(prev);
    n.has(code) ? n.delete(code) : n.add(code);
    return n;
  });
  const clearSelection = () => setSelected(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [assignBrandOpen, setAssignBrandOpen] = useState(false);
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} EBO branch(es)? This can't be undone.`)) return;
    setBulkDeleting(true);
    try {
      const codes = Array.from(selected);
      const r = await ebosApi.bulkDelete(codes);
      if (r.blocked && r.blocked.length > 0) {
        const blockedLines = r.blocked
          .map(b => `  • ${b.code} — ${b.reason} (${b.openReqCount} open)`)
          .join('\n');
        alert(`Deleted ${r.deleted} of ${r.requested} EBO branch(es).\n\n${r.blocked.length} refused:\n${blockedLines}`);
      } else {
        setMsg(`Deleted ${r.deleted} of ${r.requested} EBO branch(es)`);
        setTimeout(() => setMsg(''), 4000);
      }
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };
  const bulkAssignBrand = async (brand) => {
    try {
      const codes = Array.from(selected);
      const r = await ebosApi.bulkAssignBrand(codes, brand);
      setMsg(`Assigned brand "${brand || '— none —'}" to ${r.updated} of ${r.requested} EBO branch(es)`);
      setTimeout(() => setMsg(''), 4000);
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk assign failed');
      throw e;
    }
  };

  // Iter29: brand dropdown source is the Brand Master. Empty selection
  // = "no brand scope" (backward-compat with pre-iter29 EBOs).
  const [brandMaster, setBrandMaster] = useState([]);
  useEffect(() => { brandsApi.list({ only_active: true }).then(setBrandMaster).catch(() => setBrandMaster([])); }, []);

  const zoneOptions = useMemo(() => [...new Set(ebos.map(e => e.zone).filter(Boolean))].sort(), [ebos]);
  const stateOptions = useMemo(() => [...new Set(ebos.map(e => e.state).filter(Boolean))].sort(), [ebos]);
  const cityOptions = useMemo(() => [...new Set(ebos.map(e => e.city).filter(Boolean))].sort(), [ebos]);

  const list = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    let filtered = ebos.filter(e => {
      if (zone !== 'all' && e.zone !== zone) return false;
      if (state !== 'all' && e.state !== state) return false;
      if (city !== 'all' && e.city !== city) return false;
      if (qLower && !`${e.name || ''} ${e.code || ''} ${e.email || ''} ${e.city || ''}`.toLowerCase().includes(qLower)) return false;
      return true;
    });
    const cmp = (a, b, key, dir = 'asc') => {
      const av = (a[key] || '').toString().toLowerCase();
      const bv = (b[key] || '').toString().toLowerCase();
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    };
    switch (sort) {
      case 'name-desc': filtered = [...filtered].sort((a, b) => cmp(a, b, 'name', 'desc')); break;
      case 'code-asc': filtered = [...filtered].sort((a, b) => cmp(a, b, 'code', 'asc')); break;
      case 'code-desc': filtered = [...filtered].sort((a, b) => cmp(a, b, 'code', 'desc')); break;
      default: filtered = [...filtered].sort((a, b) => cmp(a, b, 'name', 'asc'));
    }
    return filtered;
  }, [ebos, q, zone, state, city, sort]);

  const clearFilters = () => { setQ(''); setZone('all'); setState('all'); setCity('all'); setSort('name-asc'); };
  const activeFilterCount = [q, zone !== 'all', state !== 'all', city !== 'all'].filter(Boolean).length;

  const openAdd = () => { setEditingCode(null); setDraft(DEFAULT_DRAFT); setShowModal(true); };
  const openEdit = (row) => { setEditingCode(row.code); setDraft({ ...DEFAULT_DRAFT, ...row }); setShowModal(true); };

  const save = async () => {
    try {
      if (editingCode) {
        const { code, ...patch } = draft;
        await ebosApi.update(editingCode, patch);
      } else {
        await ebosApi.add(draft);
      }
      setShowModal(false); setDraft(DEFAULT_DRAFT); setEditingCode(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete EBO ${row.code} — ${row.name}? This can’t be undone.`)) return;
    try { await ebosApi.remove(row.code); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleStatus = async (row) => {
    const next = (row.status || 'Active') === 'Active' ? 'Inactive' : 'Active';
    const verb = next === 'Inactive' ? 'DEACTIVATE' : 'REACTIVATE';
    if (!window.confirm(`${verb} EBO ${row.code} — ${row.name}?\n\nThe linked EBO login user will be ${next === 'Inactive' ? 'blocked from signing in' : 're-enabled for sign-in'}.\nExisting requisitions and Report data stay intact.`)) return;
    try { await ebosApi.update(row.code, { status: next }); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Status update failed'); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImportState({ open: true, phase: 'uploading', progress: 0, fileName: file.name, result: null, errorMsg: '' });
    try {
      const r = await ebosApi.import(file, (evt) => {
        const total = evt.total || evt.loaded;
        if (total > 0) {
          setImportState(s => ({ ...s, progress: Math.round((evt.loaded / total) * 100) }));
        }
      });
      setImportState(s => ({ ...s, phase: 'done', progress: 100, result: r }));
      const errs = (r.errors || []).length;
      setMsg(errs === 0
        ? `Imported: ${r.inserted} inserted, ${r.updated} updated`
        : `Imported ${r.inserted + r.updated} rows — ${errs} row${errs === 1 ? '' : 's'} skipped, see details.`);
      load();
      setTimeout(() => setMsg(''), 6000);
    } catch (e) {
      setImportState(s => ({ ...s, phase: 'error', errorMsg: e.response?.data?.detail || 'Import failed' }));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const canManage = ['SuperAdmin', 'Admin'].includes(user?.role);
  const canModifyOrDelete = user?.role === 'SuperAdmin';

  return (
    <div>
      <PageHeader
        title="EBO master"
        subtitle={<span>{list.length} of {ebos.length} branches shown · each branch's Email ID becomes that EBO's login username</span>}
        actions={canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            {canModifyOrDelete && selected.size > 0 && (
              <>
                <button
                  data-testid="ebo-bulk-assign-brand-btn"
                  onClick={() => setAssignBrandOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"
                  title={`Assign brand to ${selected.size} selected EBO(s)`}
                >
                  <Tag size={13} /> Assign brand ({selected.size})
                </button>
                <button
                  data-testid="ebo-bulk-delete-btn"
                  onClick={bulkDelete}
                  disabled={bulkDeleting}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA] disabled:opacity-50"
                  style={{ color: '#B84A4A' }}
                  title={`Delete ${selected.size} selected EBO branch(es)`}
                >
                  {bulkDeleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Delete selected ({selected.size})
                </button>
              </>
            )}
            <button data-testid="ebo-download-template-btn" onClick={() => downloadAuthed(ebosApi.templateUrl(), 'ebo-master-template.xlsx')} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Download size={13}/> Download template</button>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => doImport(e.target.files?.[0])} className="hidden" />
            <button data-testid="ebo-import-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Upload size={13}/> Import Excel</button>
            <button data-testid="ebo-export-btn" onClick={() => downloadAuthed(ebosApi.exportUrl(), 'ebo-master.xlsx')} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Download size={13}/> Download Excel</button>
            <button data-testid="ebo-add-btn" onClick={openAdd} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px]" style={{ background: theme.primary }}><Plus size={13}/> Add EBO</button>
          </div>
        )}
      />

      {msg && <div className="mb-3 text-[13px] px-3 py-2 rounded bg-[#E1EEE4] text-[#2f6b41]">{msg}</div>}

      <p className="text-[12px] text-[#7a6f5c] mb-3 max-w-[1100px]">
        Import matches on <span className="font-semibold text-[#232323]">EBO Branch Code (ERP)</span> — expects columns Zone, State, City, EBO Branch Code (ERP), Brand, Name of EBO Branch, Contact Number, Email ID, GST Number. Email ID must be <span className="font-mono">@futuremakeup.in</span> or <span className="font-mono">@futuremakeup.com</span> — a login account is created automatically for it.
      </p>

      {/* Filter + sort toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6f5c]" />
          <input data-testid="ebo-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / code / city…" className="fm-focus border border-[#E7E2D3] rounded-md pl-9 pr-3 py-2 text-[13px] bg-white w-full" />
        </div>
        <select data-testid="ebo-filter-zone" value={zone} onChange={e => setZone(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
          <option value="all">All zones</option>
          {zoneOptions.map(z => <option key={z} value={z}>{z}</option>)}
        </select>
        <select data-testid="ebo-filter-state" value={state} onChange={e => setState(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
          <option value="all">All states</option>
          {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="ebo-filter-city" value={city} onChange={e => setCity(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
          <option value="all">All cities</option>
          {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex items-center gap-1 pl-1">
          <ArrowUpDown size={13} className="text-[#7a6f5c]" />
          <select data-testid="ebo-sort" value={sort} onChange={e => setSort(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
            {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        {activeFilterCount > 0 && (
          <button data-testid="ebo-clear-filters" onClick={clearFilters} className="text-[12px] px-2 py-1 rounded border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] text-[#7a6f5c]">Clear filters · {activeFilterCount}</button>
        )}
      </div>

      <div className="fm-card fm-scroll-x">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> : (
          <table className="w-full text-[13.5px]" data-testid="ebo-table">
            <thead>
              <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3] whitespace-nowrap">
                {canModifyOrDelete && (
                  <th className="pl-4 pr-1 py-3 font-medium w-8">
                    <input
                      type="checkbox"
                      data-testid="ebo-select-all-checkbox"
                      checked={list.length > 0 && list.every(e => selected.has(e.code))}
                      onChange={() => {
                        const all = list.length > 0 && list.every(e => selected.has(e.code));
                        setSelected(prev => {
                          const n = new Set(prev);
                          list.forEach(e => (all ? n.delete(e.code) : n.add(e.code)));
                          return n;
                        });
                      }}
                      disabled={list.length === 0}
                      className="cursor-pointer accent-[#9C3D54] disabled:opacity-40"
                    />
                  </th>
                )}
                <th className="px-4 py-3 font-medium">Branch code</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Zone</th>
                <th className="px-4 py-3 font-medium">State</th>
                <th className="px-4 py-3 font-medium">City</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Email (login)</th>
                <th className="px-4 py-3 font-medium">GST</th>
                <th className="px-4 py-3 font-medium">Status</th>
                {canModifyOrDelete && <th className="px-4 py-3 font-medium text-right pr-6">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={canModifyOrDelete ? 11 : 9} className="px-5 py-10 text-center text-[#7a6f5c]">
                  {ebos.length === 0 ? 'No EBO branches yet — import via Excel or click Add EBO.' : 'No branches match those filters.'}
                </td></tr>
              )}
              {list.map(e => {
                const status = e.status || 'Active';
                const active = status === 'Active';
                return (
                <tr key={e.code} className="fm-row border-t border-[#EFEADC] align-top" style={active ? {} : { opacity: 0.6 }}>
                  {canModifyOrDelete && (
                    <td className="pl-4 pr-1 py-3 w-8">
                      <input
                        type="checkbox"
                        data-testid={`ebo-select-checkbox-${e.code}`}
                        checked={selected.has(e.code)}
                        onChange={() => toggleOne(e.code)}
                        className="cursor-pointer accent-[#9C3D54]"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-[13px] whitespace-nowrap">{e.code}</td>
                  <td className="px-4 py-3 font-medium min-w-[220px]">{e.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{e.zone}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{e.state}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{e.city}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-1"><Phone size={12} className="text-[#7a6f5c]"/> {e.contact}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-1"><Mail size={12} className="text-[#7a6f5c]"/> {e.email}</span></td>
                  <td className="px-4 py-3 text-[12.5px] font-mono whitespace-nowrap">{e.gst}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-full" style={active ? { background: '#E1EEE4', color: '#2f6b41' } : { background: '#F2E2E2', color: '#a04747' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? '#4D7C5F' : '#a04747' }} />
                      {status}
                    </span>
                  </td>
                  {canModifyOrDelete && (
                    <td className="px-4 py-3 pr-6 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`ebo-toggle-${e.code}`} onClick={() => toggleStatus(e)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]" title={active ? 'Deactivate' : 'Reactivate'}><Power size={12} /> {active ? 'Deactivate' : 'Activate'}</button>
                        <button data-testid={`ebo-edit-${e.code}`} onClick={() => openEdit(e)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]" title="Modify"><Pencil size={12} /> Modify</button>
                        <button data-testid={`ebo-delete-${e.code}`} onClick={() => remove(e)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]" style={{ color: '#B84A4A' }} title="Delete"><Trash2 size={12} /> Delete</button>
                      </div>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="fm-card p-5 w-full max-w-[560px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-[16px]" data-testid="ebo-modal-title">{editingCode ? `Modify EBO · ${editingCode}` : 'Add EBO'}</div>
              <button onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Branch code (ERP)" value={draft.code} onChange={v => setDraft({ ...draft, code: v })} readOnly={!!editingCode} />
              <div>
                <label className="text-[12.5px] font-medium">Zone</label>
                <select value={draft.zone} onChange={e => setDraft({ ...draft, zone: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                  <option>North</option><option>South</option><option>East</option><option>West</option>
                </select>
              </div>
              <F label="Account Code (Requisitions)" value={draft.accountCode || ''} onChange={v => setDraft({ ...draft, accountCode: v })} />
              <F label="ERP Branch Code (Sales Data)" value={draft.erpBranchCode || ''} onChange={v => setDraft({ ...draft, erpBranchCode: v })} />
              <div className="col-span-2">
                <label className="text-[12.5px] font-medium">Brand scope</label>
                <select
                  data-testid="ebo-modal-brand-select"
                  value={draft.brand || ''}
                  onChange={e => setDraft({ ...draft, brand: e.target.value })}
                  className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
                >
                  <option value="">— No brand scope (see all brands) —</option>
                  {brandMaster.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
                </select>
                <p className="text-[11.5px] text-[#7a6f5c] mt-1">Setting a brand restricts this EBO to requisition only that brand's SKUs. Leave blank for multi-brand.</p>
              </div>
              <F label="Branch name" value={draft.name} onChange={v => setDraft({ ...draft, name: v })} full />
              <F label="State" value={draft.state} onChange={v => setDraft({ ...draft, state: v })} />
              <F label="City" value={draft.city} onChange={v => setDraft({ ...draft, city: v })} />
              <F label="Contact" value={draft.contact} onChange={v => setDraft({ ...draft, contact: v })} />
              <F label="Email (@futuremakeup.in / .com)" value={draft.email} onChange={v => setDraft({ ...draft, email: v })} />
              <F label="GST Number" value={draft.gst} onChange={v => setDraft({ ...draft, gst: v })} />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="px-3 py-2 border border-[#E7E2D3] rounded-md text-[13.5px]">Cancel</button>
              <button data-testid="ebo-modal-save-btn" onClick={save} className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium" style={{ background: theme.primary }}>{editingCode ? 'Save changes' : 'Add EBO'}</button>
            </div>
          </div>
        </div>
      )}

      <BulkAssignBrandModal
        open={assignBrandOpen}
        onClose={() => setAssignBrandOpen(false)}
        onSubmit={bulkAssignBrand}
        count={selected.size}
        entityLabel="EBO branch"
        primary={theme.primary}
      />

      <ImportProgressModal
        open={importState.open}
        onClose={() => setImportState(s => ({ ...s, open: false }))}
        title="EBO Master import"
        phase={importState.phase}
        progress={importState.progress}
        fileName={importState.fileName}
        result={importState.result}
        errorMsg={importState.errorMsg}
      />
    </div>
  );
}

function F({ label, value, onChange, full, readOnly }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <label className="text-[12.5px] font-medium">{label}</label>
      <input readOnly={readOnly} value={value} onChange={e => onChange(e.target.value)} className={`fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] ${readOnly ? 'bg-[#F5F1E7] text-[#7a6f5c]' : 'bg-white'}`} />
    </div>
  );
}
